import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Sparkles, Plus, Save, Trash2, Loader2, CheckCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const AssessmentBuilderPage = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('setup');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAssessments, setSavedAssessments] = useState([]);

  const [config, setConfig] = useState({
    title: '',
    topic: '',
    description: '',
    num_questions: 10,
    question_types: ['likert', 'scenario'],
    target_audience: 'youth ages 16-24 in employment programs',
    measurement_goals: '',
    points_awarded: 25
  });

  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    fetchSaved();
  }, []);

  const fetchSaved = async () => {
    try {
      const res = await fetch(`${API}/api/ai/assessment-builder/list`, { headers: hdr() });
      if (res.ok) setSavedAssessments(await res.json());
    } catch (e) { console.error(e); }
  };

  const generateQuestions = async () => {
    if (!config.topic) { toast.error('Please enter a topic'); return; }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/ai/assessment-builder/generate`, {
        method: 'POST', headers: hdr(), body: JSON.stringify(config)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.questions?.length > 0) {
          setQuestions(data.questions);
          setStep('review');
          toast.success(`Generated ${data.questions.length} questions`);
        } else {
          toast.error('AI could not generate questions. Try different settings.');
        }
      }
    } catch (e) { toast.error('Generation failed'); }
    setGenerating(false);
  };

  const saveAssessment = async () => {
    if (!config.title) { toast.error('Please add a title'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/ai/assessment-builder/save`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ ...config, questions })
      });
      if (res.ok) {
        toast.success('Assessment saved!');
        setStep('setup');
        setQuestions([]);
        setConfig(prev => ({ ...prev, title: '', topic: '', description: '', measurement_goals: '' }));
        fetchSaved();
      }
    } catch (e) { toast.error('Save failed'); }
    setSaving(false);
  };

  const removeQuestion = (idx) => {
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  return (
    <div className="min-h-screen pb-8 overflow-x-hidden" style={{ backgroundColor: '#F5F3EF' }}>
      <div style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }} className="p-6 text-white">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <h1 className="text-xl font-bold">AI Assessment Builder</h1>
              <p style={{ color: 'rgba(255,255,255,0.7)' }} className="text-sm">Create custom assessments with AI assistance</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {step === 'setup' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Saved Assessments */}
            {savedAssessments.length > 0 && (
              <Card className="p-5 rounded-2xl bg-white">
                <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-violet-500" /> Your Assessments ({savedAssessments.length})
                </h3>
                <div className="space-y-2">
                  {savedAssessments.map((a, i) => (
                    <div key={a.id || i} className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
                      <div>
                        <p className="font-medium text-sm">{a.title}</p>
                        <p className="text-xs text-gray-500">{a.questions?.length || 0} questions • {a.topic}</p>
                      </div>
                      <Badge className={a.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}>
                        {a.is_active ? 'Active' : 'Draft'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Create New */}
            <Card className="p-5 rounded-2xl bg-white">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-500" /> Create New Assessment
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="font-semibold">Assessment Title *</Label>
                  <Input value={config.title} onChange={e => setConfig({ ...config, title: e.target.value })} placeholder="e.g., Workplace Communication Skills" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="font-semibold">Topic / Subject *</Label>
                  <Input value={config.topic} onChange={e => setConfig({ ...config, topic: e.target.value })} placeholder="e.g., communication, teamwork, financial literacy" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label className="font-semibold">What are you measuring?</Label>
                  <Textarea value={config.measurement_goals} onChange={e => setConfig({ ...config, measurement_goals: e.target.value })} placeholder="e.g., ability to handle workplace conflicts, professional email writing skills" className="mt-1 rounded-xl" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-semibold">Number of Questions</Label>
                    <Select value={String(config.num_questions)} onValueChange={v => setConfig({ ...config, num_questions: parseInt(v) })}>
                      <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[5, 10, 15, 20, 25].map(n => <SelectItem key={n} value={String(n)}>{n} questions</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-semibold">Points Awarded</Label>
                    <Select value={String(config.points_awarded)} onValueChange={v => setConfig({ ...config, points_awarded: parseInt(v) })}>
                      <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[10, 15, 20, 25, 50].map(n => <SelectItem key={n} value={String(n)}>{n} points</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="font-semibold">Description (optional)</Label>
                  <Textarea value={config.description} onChange={e => setConfig({ ...config, description: e.target.value })} placeholder="Brief description shown to youth before starting" className="mt-1 rounded-xl" rows={2} />
                </div>
                <Button onClick={generateQuestions} disabled={generating || !config.topic} className="w-full h-12 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold" data-testid="generate-btn">
                  {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> AI is generating questions...</> : <><Sparkles className="w-5 h-5 mr-2" /> Generate Questions with AI</>}
                </Button>
              </div>
            </Card>
          </motion.div>
        )}

        {step === 'review' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">Review Questions ({questions.length})</h3>
              <Button variant="outline" onClick={() => setStep('setup')} className="rounded-xl text-sm">Back to Setup</Button>
            </div>

            {questions.map((q, idx) => (
              <Card key={q.id || idx} className="p-4 rounded-2xl bg-white">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded">{idx + 1}</span>
                    <Badge className="text-[10px] bg-gray-100 text-gray-600">{q.question_type}</Badge>
                    <Badge className="text-[10px] bg-blue-50 text-blue-600">{q.category}</Badge>
                  </div>
                  <button onClick={() => removeQuestion(idx)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
                <p className="text-sm font-medium mb-2">{q.question_text}</p>
                {q.options && (
                  <div className="space-y-1">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className={`text-xs px-3 py-1.5 rounded-lg ${opt === q.correct_answer ? 'bg-green-50 text-green-700 font-medium' : 'bg-gray-50 text-gray-600'}`}>
                        {opt === q.correct_answer && <CheckCircle className="w-3 h-3 inline mr-1" />}
                        {opt}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ))}

            <div className="flex gap-3">
              <Button onClick={generateQuestions} disabled={generating} variant="outline" className="flex-1 rounded-xl">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Regenerate'}
              </Button>
              <Button onClick={saveAssessment} disabled={saving || questions.length === 0} className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold" data-testid="save-assessment-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Assessment
              </Button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AssessmentBuilderPage;
